import jsPDF from 'jspdf';
import {
  buildOrthoIndexPreviewUrl,
  DRONE_INDEX_DEFS,
  DRONE_ORTHO_BANDS,
  rateIndexParts,
} from '../constants/droneOrthoBands';
import type { DroneMissionConfig } from '../constants/dronePrescriptionLayers';
import type { DroneOrthoFieldReport, IndexStatResult } from './droneOrthoAnalytics';
import { cleanDroneIndexPreviewToDataUrl } from './droneIndexPreviewClean';
import digisakaLogo from '../../../assets/logo-transparent.png';

const GREEN: [number, number, number] = [18, 106, 55];
const GREEN_DARK: [number, number, number] = [13, 80, 41];
const TEAL: [number, number, number] = [14, 116, 144];
const TEXT: [number, number, number] = [30, 41, 59];
const SUB: [number, number, number] = [91, 102, 116];
const BG_GREEN: [number, number, number] = [232, 243, 236];
const BORDER: [number, number, number] = [226, 232, 240];

let cachedLogoDataUrl: string | null | undefined;

async function loadDigisakaLogoDataUrl(): Promise<string | null> {
  if (cachedLogoDataUrl !== undefined) return cachedLogoDataUrl;
  try {
    const res = await fetch(digisakaLogo);
    const blob = await res.blob();
    cachedLogoDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('logo read failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    cachedLogoDataUrl = null;
  }
  return cachedLogoDataUrl;
}

function drawHeader(
  pdf: jsPDF,
  report: DroneOrthoFieldReport,
  subtitle: string,
  logoDataUrl?: string | null
): void {
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const headerH = 32;

  // Brand bar — logo + Digisaka + report title (matches Digisaka report lockup)
  pdf.setFillColor(...GREEN);
  pdf.rect(0, 0, pageW, headerH, 'F');

  const logoSize = 18;
  const logoY = (headerH - logoSize) / 2;
  let textX = marginX;
  if (logoDataUrl) {
    try {
      pdf.addImage(logoDataUrl, 'PNG', marginX, logoY, logoSize, logoSize);
      textX = marginX + logoSize + 5;
    } catch {
      /* text-only fallback */
    }
  }

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text('Digisaka', textX, 13);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(subtitle, textX, 21);

  // Meta strip under brand bar (keeps green header clean like the brand lockup)
  const metaY0 = headerH;
  const metaH = 10;
  pdf.setFillColor(241, 245, 242);
  pdf.rect(0, metaY0, pageW, metaH, 'F');
  pdf.setTextColor(...SUB);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  const meta = [
    new Date(report.generatedAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    report.farmName || 'Your field',
    `About ${report.fieldAreaHa.toFixed(2)} ha`,
    `${report.missionLabel} map`,
  ].join('  ·  ');
  pdf.text(meta, marginX, metaY0 + 6.5);
}

function drawFooter(pdf: jsPDF, label: string): void {
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const footerY = 285;
  pdf.setDrawColor(...BORDER);
  pdf.line(marginX, footerY - 6, pageW - marginX, footerY - 6);
  pdf.setTextColor(...SUB);
  pdf.setFontSize(8);
  pdf.text('Digisaka · support@digisaka.app', marginX, footerY);
  pdf.setFillColor(241, 245, 242);
  pdf.roundedRect(pageW - marginX - 32, footerY - 4, 32, 6, 1.5, 1.5, 'F');
  pdf.setFontSize(7);
  pdf.text(label, pageW - marginX - 16, footerY, { align: 'center' });
}

function fmtStat(n: number, digits = 3): string {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

/** Draw mean / min / max / std / coverage for one index. Returns y after the block. */
function drawIndexStatistics(
  pdf: jsPDF,
  idx: IndexStatResult,
  x: number,
  y: number,
  width: number
): number {
  if (!idx.available) return y;

  const cells: Array<{ label: string; value: string }> = [
    { label: 'Average', value: fmtStat(idx.mean) },
    { label: 'Lowest', value: fmtStat(idx.min) },
    { label: 'Highest', value: fmtStat(idx.max) },
    { label: 'Variation', value: idx.std != null ? fmtStat(idx.std) : '—' },
    { label: 'Coverage', value: `${fmtStat(idx.validPercent, 1)}%` },
  ];

  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Statistics', x, y);
  y += 5;

  const gap = 2.5;
  const cellW = (width - gap * (cells.length - 1)) / cells.length;
  const cellH = 14;

  cells.forEach((cell, i) => {
    const cx = x + i * (cellW + gap);
    pdf.setFillColor(248, 250, 252);
    pdf.setDrawColor(...BORDER);
    pdf.setLineWidth(0.2);
    pdf.roundedRect(cx, y, cellW, cellH, 1.5, 1.5, 'FD');
    pdf.setTextColor(...SUB);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6.5);
    pdf.text(cell.label, cx + cellW / 2, y + 4.5, { align: 'center' });
    pdf.setTextColor(...TEXT);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text(cell.value, cx + cellW / 2, y + 10.5, { align: 'center' });
  });

  return y + cellH + 4;
}

function previewBboxFromReport(
  report: DroneOrthoFieldReport,
  mission: DroneMissionConfig
): [number, number, number, number] {
  const b = report.fieldBbox;
  if (!b) return mission.bbox;
  return [
    b[0] - (b[2] - b[0]) * 0.08,
    b[1] - (b[3] - b[1]) * 0.08,
    b[2] + (b[2] - b[0]) * 0.08,
    b[3] + (b[3] - b[1]) * 0.08,
  ];
}

/** A4 Digisaka field report for ONE index (per-index flow). */
export async function downloadDroneOrthoReportPdf(report: DroneOrthoFieldReport): Promise<void> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const contentW = pageW - marginX * 2;

  const idx = report.indexes.find((i) => i.available) ?? report.indexes[0];
  const def = idx ? DRONE_INDEX_DEFS.find((d) => d.key === idx.key) : null;
  const rating = idx?.available ? rateIndexParts(idx.key, idx.mean) : null;
  const p = report.parameters;

  const previewDataUrl = report.previewUrl
    ? await cleanDroneIndexPreviewToDataUrl(report.previewUrl)
    : null;
  const logoDataUrl = await loadDigisakaLogoDataUrl();

  drawHeader(pdf, report, 'Field crop health summary', logoDataUrl);

  let y = 48; // below brand bar (32) + meta strip (10) + gap
  const colGap = 8;
  const colW = (contentW - colGap) / 2;
  const leftX = marginX;
  const rightX = marginX + colW + colGap;

  pdf.setTextColor(...SUB);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.text('What we checked', leftX, y);

  pdf.setTextColor(...TEXT);
  pdf.setFontSize(13);
  pdf.text(def?.plainLabel || 'Crop check', leftX, y + 7);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...SUB);
  const descLines = pdf.splitTextToSize(
    def?.plainDescription || 'Field-level drone crop check.',
    colW
  );
  pdf.text(descLines, leftX, y + 13);

  const badgeY = y + 13 + descLines.length * 4 + 4;
  pdf.setFillColor(...BG_GREEN);
  pdf.roundedRect(leftX, badgeY, colW, 22, 3, 3, 'F');
  pdf.setTextColor(...GREEN_DARK);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.text('Result', leftX + 4, badgeY + 6);
  pdf.setFontSize(14);
  pdf.text(rating?.level || '—', leftX + 4, badgeY + 13);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  const detailLines = pdf.splitTextToSize(rating?.detail || '', colW - 8);
  pdf.text(detailLines, leftX + 4, badgeY + 18);

  const legendY = badgeY + 28;
  pdf.setFillColor(47, 158, 90);
  pdf.circle(leftX + 2, legendY, 1.5, 'F');
  pdf.setTextColor(...SUB);
  pdf.setFontSize(8);
  pdf.text('Healthier, more cover', leftX + 5, legendY + 1);
  pdf.setFillColor(224, 160, 32);
  pdf.circle(leftX + 48, legendY, 1.5, 'F');
  pdf.text('Weaker spots', leftX + 51, legendY + 1);

  const previewH = 52;
  pdf.setFillColor(0, 0, 0);
  pdf.roundedRect(rightX, y, colW, previewH, 3, 3, 'F');
  if (previewDataUrl) {
    try {
      pdf.addImage(previewDataUrl, 'PNG', rightX + 1, y + 1, colW - 2, previewH - 2, undefined, 'FAST');
    } catch {
      /* keep black */
    }
  } else {
    pdf.setTextColor(180, 180, 180);
    pdf.setFontSize(8);
    pdf.text('Field drone preview', rightX + colW / 2, y + previewH / 2, { align: 'center' });
  }
  pdf.setTextColor(...SUB);
  pdf.setFontSize(7.5);
  pdf.text('Colored areas are your crop check on the map', rightX + colW / 2, y + previewH + 5, {
    align: 'center',
  });

  y = Math.max(legendY + 8, y + previewH + 10);
  pdf.setDrawColor(...BORDER);
  pdf.setLineWidth(0.3);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 8;

  if (idx?.available) {
    y = drawIndexStatistics(pdf, idx, marginX, y, contentW);
    pdf.setDrawColor(...BORDER);
    pdf.line(marginX, y, pageW - marginX, y);
    y += 8;
  }

  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('Simple field insights', marginX, y);
  y += 8;

  const insightW = (contentW - 12) / 3;
  const insights: Array<{ title: string; body: string }> = [
    {
      title: 'Plant cover',
      body:
        p.canopyCoverPercent != null
          ? `About ${p.canopyCoverPercent.toFixed(0)}% of the field looks well covered with plants.`
          : 'Not estimated for this check. Run Plant greenness to estimate cover.',
    },
    {
      title: 'Plant height',
      body: 'Not available yet. Needs a special height map from the drone flight.',
    },
    {
      title: 'Leaf greenness',
      body:
        p.chlorophyllProxy != null
          ? `Rating: ${rateIndexParts(idx?.key === 'ndre' ? 'ndre' : 'ndvi', p.chlorophyllProxy).level}, similar to overall result.`
          : 'Not estimated for this check.',
    },
  ];

  insights.forEach((ins, i) => {
    const x = marginX + i * (insightW + 6);
    pdf.setFillColor(...TEAL);
    pdf.circle(x + 2, y, 1.2, 'F');
    pdf.setTextColor(...TEXT);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.text(ins.title, x + 6, y + 1);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...SUB);
    pdf.setFontSize(8);
    const body = pdf.splitTextToSize(ins.body, insightW);
    pdf.text(body, x, y + 6);
  });

  y += 28;
  pdf.setDrawColor(...BORDER);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 8;

  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('What you can do next', marginX, y);
  y += 8;

  const nextSteps = [
    'Walk the yellow or red spots for water, fertilizer, or pest checks.',
    'Compare with how the crop looks in the field.',
    'Contact Digisaka for a new drone flight if needed.',
  ];
  nextSteps.forEach((step, i) => {
    const x = marginX + i * (insightW + 6);
    pdf.setFillColor(...TEAL);
    pdf.circle(x + 2, y, 1.2, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...TEXT);
    pdf.setFontSize(8);
    const lines = pdf.splitTextToSize(step, insightW);
    pdf.text(lines, x + 6, y + 1);
  });

  drawFooter(pdf, 'Field report');
  const safeName = (report.farmName || report.missionKey).replace(/[^\w.-]+/g, '_');
  pdf.save(`Digisaka_field_summary_${safeName}.pdf`);
}

async function previewForIndex(
  report: DroneOrthoFieldReport,
  mission: DroneMissionConfig,
  index: IndexStatResult
): Promise<string | null> {
  if (!index.available) return null;
  const bands = DRONE_ORTHO_BANDS[mission.key];
  const url = buildOrthoIndexPreviewUrl(mission.orthoCogUrl, index.key, bands, {
    bbox: previewBboxFromReport(report, mission),
    maxSize: 640,
  });
  if (!url) return null;
  return cleanDroneIndexPreviewToDataUrl(url);
}

/**
 * Full A4 report with ALL indexes (NDVI, NDRE, ExG, SAVI) in one PDF.
 * Does not require picking a single index first.
 */
export async function downloadDroneOrthoFullReportPdf(
  report: DroneOrthoFieldReport,
  mission: DroneMissionConfig
): Promise<void> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const marginX = 16;
  const contentW = pageW - marginX * 2;
  const p = report.parameters;
  const logoDataUrl = await loadDigisakaLogoDataUrl();

  drawHeader(pdf, report, 'Full field crop health report', logoDataUrl);

  let y = 48;
  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('All crop checks at a glance', marginX, y);
  y += 8;

  for (const idx of report.indexes.filter((i) => i.available)) {
    const def = DRONE_INDEX_DEFS.find((d) => d.key === idx.key);
    const label = def?.plainLabel || idx.label;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...TEXT);
    pdf.text(label, marginX, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    const rating = rateIndexParts(idx.key, idx.mean);
    pdf.setTextColor(...GREEN_DARK);
    const resultLine = pdf.splitTextToSize(
      `${rating.level} — avg ${fmtStat(idx.mean)} (min ${fmtStat(idx.min)}, max ${fmtStat(idx.max)})`,
      contentW - 55
    );
    pdf.text(resultLine, marginX + 52, y);
    y += Math.max(7, resultLine.length * 4 + 2);
  }

  y += 4;
  pdf.setDrawColor(...BORDER);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 8;

  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('Simple field insights', marginX, y);
  y += 8;

  const insightW = (contentW - 12) / 3;
  const insights = [
    {
      title: 'Plant cover',
      body:
        p.canopyCoverPercent != null
          ? `About ${p.canopyCoverPercent.toFixed(0)}% of the field looks well covered with plants.`
          : 'Not estimated (needs Plant greenness).',
    },
    {
      title: 'Plant height',
      body: 'Not available yet. Needs a special height map from the drone flight.',
    },
    {
      title: 'Leaf greenness',
      body:
        p.chlorophyllProxy != null
          ? `Rating: ${rateIndexParts(
              report.indexes.some((i) => i.key === 'ndre' && i.available) ? 'ndre' : 'ndvi',
              p.chlorophyllProxy
            ).level}.`
          : 'Not estimated for this field.',
    },
  ];
  insights.forEach((ins, i) => {
    const x = marginX + i * (insightW + 6);
    pdf.setFillColor(...TEAL);
    pdf.circle(x + 2, y, 1.2, 'F');
    pdf.setTextColor(...TEXT);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.text(ins.title, x + 6, y + 1);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...SUB);
    pdf.setFontSize(8);
    pdf.text(pdf.splitTextToSize(ins.body, insightW), x, y + 6);
  });

  y += 26;
  pdf.setDrawColor(...BORDER);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 8;

  pdf.setTextColor(...TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('What you can do next', marginX, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...SUB);
  [
    'Walk yellow or red spots for water, fertilizer, or pest checks.',
    'Compare with how the crop looks in the field.',
    'Contact Digisaka for a new drone flight if needed.',
  ].forEach((line) => {
    pdf.text(`• ${line}`, marginX, y);
    y += 5;
  });

  drawFooter(pdf, 'Full report');

  for (const idx of report.indexes.filter((i) => i.available)) {
    const def = DRONE_INDEX_DEFS.find((d) => d.key === idx.key);
    pdf.addPage();
    drawHeader(pdf, report, def?.plainLabel || idx.label, logoDataUrl);

    y = 48;
    pdf.setTextColor(...SUB);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('What we checked', marginX, y);
    pdf.setTextColor(...TEXT);
    pdf.setFontSize(14);
    pdf.text(def?.plainLabel || idx.label, marginX, y + 8);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...SUB);
    pdf.text(pdf.splitTextToSize(def?.plainDescription || '', contentW), marginX, y + 14);

    y += 28;

    const rating = rateIndexParts(idx.key, idx.mean);
    pdf.setFillColor(...BG_GREEN);
    pdf.roundedRect(marginX, y, contentW * 0.45, 24, 3, 3, 'F');
    pdf.setTextColor(...GREEN_DARK);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('Result', marginX + 4, y + 7);
    pdf.setFontSize(14);
    pdf.text(rating.level, marginX + 4, y + 15);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text(rating.detail, marginX + 4, y + 21);

    const previewH = 70;
    const previewX = marginX + contentW * 0.48;
    const previewW = contentW * 0.52;
    pdf.setFillColor(0, 0, 0);
    pdf.roundedRect(previewX, y - 4, previewW, previewH, 3, 3, 'F');
    const previewDataUrl = await previewForIndex(report, mission, idx);
    if (previewDataUrl) {
      try {
        pdf.addImage(
          previewDataUrl,
          'PNG',
          previewX + 1,
          y - 3,
          previewW - 2,
          previewH - 2,
          undefined,
          'FAST'
        );
      } catch {
        /* black frame */
      }
    }
    pdf.setTextColor(...SUB);
    pdf.setFontSize(7.5);
    pdf.text('Colored areas are your crop check on the map', previewX + previewW / 2, y + previewH + 2, {
      align: 'center',
    });

    y = Math.max(y + 26, y + previewH + 8);
    y = drawIndexStatistics(pdf, idx, marginX, y, contentW);

    drawFooter(pdf, def?.label || idx.label);
  }

  const safeName = (report.farmName || report.missionKey).replace(/[^\w.-]+/g, '_');
  pdf.save(`Digisaka_full_field_report_${safeName}.pdf`);
}
